// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { PolicyMetadataEditHistory } from '../types/policyMetadata';
import type { ProviderSubmissionDTO, ProviderAccessTokenDTO } from '../types/providerAccess';
import type { PolicyLogEntry } from '../types/policyLog';

export function normalizeEditHistory(
  entries: PolicyMetadataEditHistory[],
  resolveUser?: (id: string) => string
): PolicyLogEntry[] {
  return entries.map((entry) => {
    const resolvedName = resolveUser ? resolveUser(entry.changedBy) : entry.changedBy;
    const user = entry.onBehalfOfAdvisorName
      ? `${resolvedName} (on behalf of ${entry.onBehalfOfAdvisorName})`
      : resolvedName;

    let details: string;
    switch (entry.changeType) {
      case 'CREATE':
        details = `Added ${entry.fieldName}`;
        if (entry.newValue) details += `: ${entry.newValue}`;
        break;
      case 'UPDATE':
        details = `Updated ${entry.fieldName}`;
        if (entry.oldValue && entry.newValue) {
          details += ` from '${entry.oldValue}' to '${entry.newValue}'`;
        } else if (entry.newValue) {
          details += ` to '${entry.newValue}'`;
        }
        break;
      case 'DELETE':
        details = `Removed ${entry.fieldName}`;
        if (entry.oldValue) details += ` (was '${entry.oldValue}')`;
        break;
      default:
        details = `Changed ${entry.fieldName}`;
    }

    if (entry.changeReason) {
      details += `. Reason: ${entry.changeReason}`;
    }

    return {
      id: `edit-${entry.id}`,
      date: entry.changedAt,
      user,
      details,
    };
  });
}

export function normalizeSubmissions(submissions: ProviderSubmissionDTO[]): PolicyLogEntry[] {
  return submissions.map((sub) => {
    let details: string;
    switch (sub.submissionType) {
      case 'FORM':
        details = 'Data returned by provider';
        break;
      case 'DOCUMENT':
        details = sub.documentFilename
          ? `Document uploaded: ${sub.documentFilename}`
          : 'Document uploaded by provider';
        break;
      case 'REJECTION':
        details = sub.rejectionReason
          ? `LOA rejected: ${sub.rejectionReason}`
          : 'LOA rejected by provider';
        break;
      case 'FEEDBACK':
        details = 'Provider feedback submitted';
        break;
      default:
        details = 'Provider submission received';
    }

    return {
      id: `sub-${sub.id}`,
      date: sub.submittedAt,
      user: 'Provider',
      details,
    };
  });
}

export function normalizeAccessTokens(
  tokens: ProviderAccessTokenDTO[],
  resolveUser?: (id: string) => string
): PolicyLogEntry[] {
  const entries: PolicyLogEntry[] = [];

  for (const token of tokens) {
    entries.push({
      id: `token-${token.id}`,
      date: token.createdAt,
      user: 'System',
      details: 'Access token generated for provider',
    });

    if (token.status === 'REVOKED' && token.revokedAt) {
      const revokedBy = token.revokedBy || 'System';
      entries.push({
        id: `token-revoke-${token.id}`,
        date: token.revokedAt,
        user: resolveUser ? resolveUser(revokedBy) : revokedBy,
        details: 'Provider access token revoked',
      });
    }
  }

  return entries;
}

export function mergeAndSort(...arrays: PolicyLogEntry[][]): PolicyLogEntry[] {
  return arrays
    .flat()
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}
